// ============================================================
// CombatantGallery - pick a combatant by looking at them.
//
// BOTH pickers since v8.240.0. The gallery began as the target
// side's alone, with the actor kept on a deliberate plain dropdown
// ("choosing who you ARE deserves less theatre than choosing who you
// shoot") - the user reversed that ruling on playtest (ADR
// 2026-08-30): one picker, parameterised, not forked. The defaults
// ARE the target side (red, 'Choose target', no none-card); the
// actor passes gold, 'Choose character' and allowNone.
//
// Each card carries its body plate's figure STROKED BY HEALTH - the
// cheapest possible "how hurt is this one" signal (v7.10.1; the plate's
// thumbnail and crowd since plan body-map P4, P8).
//
// GROUPED BY TEAM since v8.314.0: a stencil header per team in its
// livery, other teams first and the actor's own team LAST - the side you
// shoot at least is the one you scroll to. The cursor still walks ONE
// flat list in that order; the headers are presentation.
//
// The grid keeps role="listbox" with role="option" cards. That is the
// correct screen-reader semantic for single-select, and it means every
// existing `getByRole('listbox', { name: 'Roster' })` assertion
// survives - which is also what let the actor side swap in with the
// roster specs untouched.
// ============================================================
import { memo, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import clsx from '../utils/clsx';
import { onActivateKey } from '../utils/keyActivation';
import { projectDefender } from '../engine/roster/projection';
import { healthColor, vitalsOf } from '../engine/healthRamp';
import PlateThumb from './plate/PlateThumb';
import { figureFor, NOBODY } from '../engine/plate/figureFor';
import { hurtPlaces } from '../engine/plate/plateModel';
import { getBodyConfig } from '../engine/bodyConfig';
import { groupByTeam } from '../engine/roster/team';
import type { Livery } from '../engine/roster/team';
import { liveryStyle } from '../utils/liveryStyle';
import CombatantVitals from './CombatantVitals';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import type { Combatant } from '../engine/roster/types';
import common from '../styles/common.module.css';
import styles from '../styles/CombatantGallery.module.css';

export interface CombatantGalleryProps {
  roster: Combatant[];
  value: string | null;
  /** null only ever arrives from the No-character card (`allowNone`). */
  onSelect: (id: string | null) => void;
  onClose: () => void;
  /** Marks whoever you are acting as, so self-targeting is deliberate. */
  actorId?: string | null;
  allowManage?: boolean;
  onAdd?: () => void;
  onRemove?: (id: string) => void;
  onSaveToLibrary?: (id: string) => void;
  /** Open the Change team dialog. Absent for a viewer who may not edit sheets. */
  onChangeTeams?: () => void;
  /** Both sides use the gallery since v8.240.0 - the defaults ARE the target side. */
  title?: string;
  /** Load-bearing dialog name - specs address the gallery by it. */
  ariaLabel?: string;
  /** danger = the target's red; accent = the actor's gold. */
  tone?: 'danger' | 'accent';
  /** Offer a 'No character' card - acting as nobody is legal. */
  allowNone?: boolean;
}

/**
 * One card. MEMOIZED on the combatant: a single wound tick would
 * otherwise re-render every card in a twenty-strong roster, and
 * projectDefender is identity-cached so an unchanged combatant is free.
 */
const GalleryCard = memo(function GalleryCard({
  combatant,
  selected,
  isActor,
  index,
  livery,
  onSelect,
  onRemove,
  onSaveToLibrary,
  cardRef,
}: {
  combatant: Combatant;
  selected: boolean;
  isActor: boolean;
  index: number;
  /** The team's colour on the card's left edge; none for the teamless. */
  livery: Livery | null;
  onSelect: (id: string) => void;
  onRemove?: (id: string) => void;
  onSaveToLibrary?: (id: string) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}) {
  const target = projectDefender(combatant);
  const dead = (target.conditions || []).includes('K');

  return (
    <div
      ref={cardRef}
      role="option"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      data-index={index}
      data-testid={`gallery-card-${combatant.id}`}
      data-livery={livery ?? undefined}
      style={liveryStyle(livery)}
      className={clsx(styles.card, selected && styles.selected, dead && styles.dead, livery && styles.liveried)}
      onClick={() => onSelect(combatant.id)}
      onKeyDown={onActivateKey(() => onSelect(combatant.id))}
    >
      <CardThumb combatant={combatant} isActor={isActor} />

      <div className={styles.name} title={combatant.name}>
        {combatant.name}
      </div>
      <CombatantVitals target={target} name={combatant.name} />

      <CardActions combatant={combatant} onRemove={onRemove} onSaveToLibrary={onSaveToLibrary} />
    </div>
  );
});

/**
 * The body STROKED BY HEALTH - its plate's figure, one of what a horde is made of (a whole crowd at card size is
 * specks; the health colour and the vitals under it say how far it has thinned) - and the actor's mark.
 */
function CardThumb({ combatant, isActor }: { combatant: Combatant; isActor: boolean }) {
  const target = projectDefender(combatant);
  const { shown, total } = vitalsOf(target);
  return (
    <div className={styles.thumb}>
      <PlateThumb
        body={figureFor(target, combatant.sheet)}
        colour={healthColor(shown, total)}
        ariaLabel={`${combatant.name} silhouette`}
        className={styles.thumbSvg}
        hurt={hurtPlaces(target.conditions ?? [], getBodyConfig(target.bodyType || 'humanoid'))}
      />
      {isActor && (
        <span className={styles.actorMark} title="You are acting as this combatant">
          ●
        </span>
      )}
    </div>
  );
}

interface CardActionsProps {
  combatant: Combatant;
  onRemove?: (id: string) => void;
  onSaveToLibrary?: (id: string) => void;
}

/** Save to the library and remove - neither also selects the card. */
function CardActions({ combatant, onRemove, onSaveToLibrary }: CardActionsProps) {
  return (
    <div className={styles.cardActions}>
      {onSaveToLibrary && (
        <button
          type="button"
          className={styles.action}
          aria-label={`Save ${combatant.name} to library`}
          title={`Save ${combatant.name} to the character library`}
          onClick={(e) => {
            e.stopPropagation();
            onSaveToLibrary(combatant.id);
          }}
        >
          {'⤓'}
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          className={clsx(styles.action, styles.remove)}
          aria-label={`Remove ${combatant.name}`}
          title={`Remove ${combatant.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(combatant.id);
          }}
        >
          {'✕'}
        </button>
      )}
    </div>
  );
}

/**
 * Where the cursor starts: the none-card when acting as nobody, else the
 * selected combatant's card, else the first.
 */
function startIndexOf(ordered: Combatant[], value: string | null, allowNone: boolean): number {
  if (allowNone && value === null) return 0;
  const rosterIndex = ordered.findIndex((c) => c.id === value);
  return rosterIndex < 0 ? 0 : rosterIndex + (allowNone ? 1 : 0);
}

/**
 * The gallery's keys. Escape closes and goes no further; the arrows walk
 * the one flat list with wrap, Home and End jump to its ends.
 */
function galleryKeys(step: (delta: number) => void, jump: (to: 'first' | 'last') => void, onClose: () => void) {
  const moves: Record<string, () => void> = {
    ArrowRight: () => step(1),
    ArrowDown: () => step(1),
    ArrowLeft: () => step(-1),
    ArrowUp: () => step(-1),
    Home: () => jump('first'),
    End: () => jump('last'),
  };
  return (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    move();
  };
}

export default function CombatantGallery({
  roster,
  value,
  onSelect,
  onClose,
  actorId = null,
  allowManage = false,
  onAdd,
  onRemove,
  onSaveToLibrary,
  onChangeTeams,
  title = '⌖ Choose target',
  ariaLabel = 'Choose target',
  tone = 'danger',
  allowNone = false,
}: CombatantGalleryProps) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(onClose, trapRef);
  // Other teams first, own team last; one flat list underneath for the
  // cursor, with each card's position in it.
  const groups = useMemo(() => groupByTeam(roster, actorId), [roster, actorId]);
  const ordered = useMemo(() => groups.flatMap((g) => g.members), [groups]);
  const { count, onKeyDown, cardRef } = useGalleryCursor(ordered, value, allowNone, onClose);
  const pick = (id: string | null) => {
    onSelect(id);
    onClose();
  };

  return (
    <div
      className={common.modal}
      onClick={backdrop.onBackdropClick}
      onKeyDown={onKeyDown}
      data-testid="combatant-gallery"
      role="presentation"
    >
      <div
        className={clsx(common.modalContent, styles.sheet, tone === 'accent' && styles.accent)}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <h3 className={styles.title}>{title}</h3>
        {count === 0 ? (
          <div className={styles.empty}>Nobody in the fight yet</div>
        ) : (
          <RosterGrid
            groups={groups}
            ordered={ordered}
            value={value}
            actorId={actorId}
            allowNone={allowNone}
            onPick={pick}
            onRemove={removerFor(allowManage, onRemove, roster)}
            onSaveToLibrary={onSaveToLibrary}
            cardRef={cardRef}
          />
        )}
        <GalleryFooter onAdd={allowManage ? onAdd : undefined} onChangeTeams={onChangeTeams} onClose={onClose} />
      </div>
    </div>
  );
}

/**
 * The gallery's cursor over ONE flat list: with allowNone the 'No
 * character' card holds index 0 and every roster card shifts by one. Real
 * focus follows the cursor, so the arrow keys move the screen reader's
 * attention rather than only a highlight.
 */
function useGalleryCursor(ordered: Combatant[], value: string | null, allowNone: boolean, onClose: () => void) {
  const cards = useRef<(HTMLDivElement | null)[]>([]);
  const count = ordered.length + (allowNone ? 1 : 0);
  const [cursor, setCursor] = useState(() => startIndexOf(ordered, value, allowNone));
  useEffect(() => {
    cards.current[cursor]?.focus();
  }, [cursor]);
  const step = (delta: number) => {
    if (count === 0) return;
    setCursor((c) => (((c + delta) % count) + count) % count);
  };
  const jump = (to: 'first' | 'last') => setCursor(to === 'first' ? 0 : count - 1);
  const cardRef = (i: number) => (el: HTMLDivElement | null) => {
    cards.current[i] = el;
  };
  return { count, onKeyDown: galleryKeys(step, jump, onClose), cardRef };
}

/** Removal is offered only when managing, and never for the last combatant standing. */
function removerFor(allowManage: boolean, onRemove: ((id: string) => void) | undefined, roster: Combatant[]) {
  return allowManage && onRemove && roster.length > 1 ? onRemove : undefined;
}

interface RosterGridProps {
  groups: ReturnType<typeof groupByTeam>;
  ordered: Combatant[];
  value: string | null;
  actorId: string | null;
  allowNone: boolean;
  onPick: (id: string | null) => void;
  onRemove?: (id: string) => void;
  onSaveToLibrary?: (id: string) => void;
  cardRef: (i: number) => (el: HTMLDivElement | null) => void;
}

/** The listbox: the none-card first when offered, then each team's header and cards. */
function RosterGrid({ groups, ordered, value, actorId, allowNone, onPick, cardRef, ...actions }: RosterGridProps) {
  const offset = allowNone ? 1 : 0;
  return (
    <div className={styles.grid} role="listbox" aria-label="Roster">
      {allowNone && <NoneCard selected={value === null} onPick={() => onPick(null)} cardRef={cardRef(0)} />}
      {groups.map((group) => (
        // A fragment per group: the header spans the grid, the cards
        // flow on. Keyed by tag so a team's header is stable.
        <div key={group.team?.tag ?? 'none'} className={styles.group} role="presentation">
          {groups.length > 1 && <TeamHeader group={group} />}
          {group.members.map((c) => {
            const i = ordered.indexOf(c) + offset;
            return (
              <GalleryCard
                key={c.id}
                combatant={c}
                index={i}
                selected={c.id === value}
                isActor={c.id === actorId}
                livery={group.livery}
                onSelect={onPick}
                onRemove={actions.onRemove}
                onSaveToLibrary={actions.onSaveToLibrary}
                cardRef={cardRef(i)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** + Add combatant (when managing) and Close. Adding closes the gallery too. */
interface GalleryFooterProps {
  onAdd?: () => void;
  onChangeTeams?: () => void;
  onClose: () => void;
}

function GalleryFooter({ onAdd, onChangeTeams, onClose }: GalleryFooterProps) {
  return (
    <div className={styles.footer}>
      {onChangeTeams && (
        <button
          type="button"
          className={common.btnSmall}
          data-testid="gallery-change-teams"
          onClick={() => {
            onChangeTeams();
            onClose();
          }}
        >
          Change team...
        </button>
      )}
      {onAdd && (
        <button
          type="button"
          className={common.btnSmall}
          onClick={() => {
            onAdd();
            onClose();
          }}
        >
          + Add combatant
        </button>
      )}
      <button type="button" className={common.btnSmall} aria-label="Close" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

interface NoneCardProps {
  selected: boolean;
  onPick: () => void;
  cardRef: (el: HTMLDivElement | null) => void;
}

/** 'No character' - acting as nobody is legal (the actor side's `allowNone`). */
function NoneCard({ selected, onPick, cardRef }: NoneCardProps) {
  return (
    <div
      ref={cardRef}
      role="option"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      data-testid="gallery-card-none"
      className={clsx(styles.card, styles.noneCard, selected && styles.selected)}
      onClick={onPick}
      onKeyDown={onActivateKey(onPick)}
    >
      <div className={styles.thumb}>
        <PlateThumb
          body={NOBODY}
          colour="var(--text-faint)"
          ariaLabel="No character silhouette"
          className={styles.thumbSvg}
          opacity={0.3}
        />
      </div>
      <div className={styles.name}>No character</div>
    </div>
  );
}

/** A team's stencil header, in its livery. */
function TeamHeader({ group }: { group: ReturnType<typeof groupByTeam>[number] }) {
  return (
    <div
      className={styles.teamHeader}
      data-testid={`gallery-team-${group.team?.tag ?? 'none'}`}
      data-livery={group.livery ?? undefined}
      style={liveryStyle(group.livery)}
      role="presentation"
    >
      {group.team?.label ?? 'No team'}
    </div>
  );
}
